// src/pages/Dashboard.jsx

import React, { useState, useMemo } from 'react';
import { LoadingSpinner } from '../App';
import { COLORS } from '../constants';
import { useDashboardData } from '../hooks/useDashboardData';
import {
  computeStats,
  buildTechActivity,
  getRowDate,
  normaliseDate,
} from '../utils/statistics';
import {
  MetricCard,
  StatusCard,
  ConditionCard,
  MiniMetricTile,
  FilterBar,
  TechnicianActivityTable,
  InventoryIntelligence,
  SystemParameterManager,
  ActivityDateChart,
} from '../components/dashboard';
import { PAGE } from '../components/ui';
import {
  CablingImpactMetrics,
  CablingProgressBar,
  CablingPriorityList,
  CablingCompletionForecast,
} from '../components/cabling';

export default function Dashboard() {
  const {
    data = [],
    doaData = [],
    cablingData = [],
    fibreCablingData = [],
    totalAssetsCount,
    loading,
    refreshing,
    error,
    refreshData,
  } = useDashboardData();

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filterRowsBySearchAndDate = (rows = []) => {
    return rows.filter((row) => {
      const isoDate = normaliseDate(getRowDate(row));

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const hasSearchMatch = Object.values(row).some((value) =>
          String(value).toLowerCase().includes(term)
        );

        if (!hasSearchMatch) return false;
      }

      if (dateFrom || dateTo) {
        if (!isoDate) return false;
        if (dateFrom && isoDate < dateFrom) return false;
        if (dateTo && isoDate > dateTo) return false;
      }

      return true;
    });
  };

  const filteredData = useMemo(
    () => filterRowsBySearchAndDate(data),
    [data, searchTerm, dateFrom, dateTo]
  );

  const filteredDoaData = useMemo(
    () => filterRowsBySearchAndDate(doaData),
    [doaData, searchTerm, dateFrom, dateTo]
  );

  const filteredCablingData = useMemo(
    () => filterRowsBySearchAndDate(cablingData),
    [cablingData, searchTerm, dateFrom, dateTo]
  );

  const filteredFibreCablingData = useMemo(
    () => filterRowsBySearchAndDate(fibreCablingData),
    [fibreCablingData, searchTerm, dateFrom, dateTo]
  );

  const allIsoDates = useMemo(() => {
    const allRows = [
      ...data,
      ...doaData,
      ...cablingData,
      ...fibreCablingData,
    ];

    return Array.from(
      new Set(
        allRows
          .map((row) => normaliseDate(getRowDate(row)))
          .filter(Boolean)
      )
    ).sort();
  }, [data, doaData, cablingData, fibreCablingData]);

  const stats = useMemo(() => {
    if (filteredData.length === 0) return null;
    return computeStats(filteredData);
  }, [filteredData]);

  const activities = useMemo(() => {
    return buildTechActivity(filteredData);
  }, [filteredData]);

  const safeStatsTotal = stats?.total || 0;

  const hasCablingData =
    filteredCablingData.length > 0 || filteredFibreCablingData.length > 0;

  const COMPACT = {
    pagePaddingY: Math.max(8, Math.round(PAGE.paddingY * 0.5)),
    pagePaddingX: Math.max(12, Math.round(PAGE.paddingX * 0.6)),
    sectionGap: 8,
    blockGap: 10,
    wideGap: 12,
  };

  if (loading) {
    return (
      <LoadingSpinner
        title="Loading Dashboard"
        subtitle="Preparing dashboard insights..."
      />
    );
  }

  if (error) {
    return (
      <div style={{ color: COLORS.red, padding: 60, textAlign: 'center' }}>
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        background: PAGE.colors.bg,
        fontFamily: PAGE.fontFamily,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: PAGE.maxWidth,
          margin: '0 auto',
          padding: `${COMPACT.pagePaddingY}px ${COMPACT.pagePaddingX}px`,
          boxSizing: 'border-box',
        }}
      >
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
          availableDates={allIsoDates}
          refreshing={refreshing}
          onClear={() => {
            setSearchTerm('');
            setDateFrom('');
            setDateTo('');
          }}
          onRefresh={refreshData}
        />

        <div style={{ marginBottom: COMPACT.blockGap }}>
          <ActivityDateChart
            data={filteredData}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            allDates={allIsoDates}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: COMPACT.sectionGap,
            marginBottom: COMPACT.blockGap,
          }}
        >
          <MetricCard
            title="Total Assets"
            value={totalAssetsCount || 0}
            subtitle="from asset sheet"
            icon="building"
          />

          {stats && safeStatsTotal > 0 && (
            <>
              <MetricCard
                title="Scan Rate"
                value={`${Math.round((stats.scanned / safeStatsTotal) * 100)}%`}
                subtitle={`${stats.scanned}/${safeStatsTotal}`}
                icon="scan"
                color={COLORS.cyan}
              />

              <MetricCard
                title="Score"
                value={`${Math.round(
                  ((safeStatsTotal - stats.condDmg) / safeStatsTotal) * 100
                )}%`}
                subtitle={`${stats.condDmg} damaged`}
                icon="heart"
                color={stats.condDmg === 0 ? COLORS.success : COLORS.warning}
              />

              <MetricCard
                title="Accessories"
                value={stats.powerCord + stats.rails}
                subtitle={`${stats.powerCord} cords, ${stats.rails} rails`}
                icon="tool"
                color={COLORS.violet}
              />
            </>
          )}
        </div>

        {stats && safeStatsTotal > 0 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr)',
                gap: COMPACT.sectionGap,
                marginBottom: COMPACT.blockGap,
              }}
            >
              <StatusCard
                title="PSU-1 Status"
                good={stats.psu1Good}
                damaged={stats.psu1Dmg}
                empty={stats.psu1Empty}
                total={safeStatsTotal}
              />

              <StatusCard
                title="PSU-2 Status"
                good={stats.psu2Good}
                damaged={stats.psu2Dmg}
                empty={stats.psu2Empty}
                total={safeStatsTotal}
              />

              <ConditionCard
                good={stats.condGood}
                damaged={stats.condDmg}
                empty={stats.condEmpty}
                total={safeStatsTotal}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: COMPACT.sectionGap,
                marginBottom: COMPACT.blockGap,
              }}
            >
              <MiniMetricTile
                value={stats.powerCord}
                total={safeStatsTotal}
                label="Power Cord Availability"
              />

              <MiniMetricTile
                value={stats.rails}
                total={safeStatsTotal}
                label="Rails Availability"
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)',
                gap: COMPACT.wideGap,
                marginBottom: COMPACT.blockGap,
              }}
            >
              <TechnicianActivityTable activities={activities} />
              <InventoryIntelligence data={filteredData} />
            </div>
          </>
        )}

        {hasCablingData && (
          <div style={{ marginTop: COMPACT.wideGap }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: COMPACT.blockGap,
                paddingBottom: 10,
                borderBottom: `2px solid ${COLORS.line}`,
              }}
            >
              <span style={{ fontSize: 20 }}>🔌</span>
              <div>
                <h2
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    margin: 0,
                    color: COLORS.text,
                  }}
                >
                  Cabling Intelligence
                </h2>
                <p
                  style={{
                    fontSize: 11,
                    margin: '3px 0 0 0',
                    color: COLORS.textSecondary,
                  }}
                >
                  Key metrics and actionable insights
                </p>
              </div>
            </div>

            <CablingImpactMetrics
              copperData={filteredCablingData}
              fibreData={filteredFibreCablingData}
            />

            <CablingProgressBar
              copperData={filteredCablingData}
              fibreData={filteredFibreCablingData}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: COMPACT.wideGap,
                marginBottom: COMPACT.blockGap,
              }}
            >
              <CablingPriorityList
                copperData={filteredCablingData}
                fibreData={filteredFibreCablingData}
              />

              <CablingCompletionForecast
                copperData={filteredCablingData}
                fibreData={filteredFibreCablingData}
              />
            </div>
          </div>
        )}

        <div style={{ marginTop: COMPACT.wideGap }}>
          <SystemParameterManager doaData={filteredDoaData} />
        </div>
      </div>
    </div>
  );
}